import React, { useState, useEffect } from "react";
import { IntlShape, useIntl } from "react-intl";
import clsx from "clsx";
import { shallowEqual } from "react-redux";

import { ModalComponent } from "@app/core/modal";
import { DeleteModal } from "@app/modules/delete-modal/delete-modal.component";
import { OutFeaturedCategoriesDTO } from "@app/api/generated";
import { Table } from "@app/components/table/table";
import { Column } from "@webbio/components";
import { useAppSelector, useAppDispatch } from "@app/redux/store";
import { featuredCategoriesThunk } from "@app/redux/thunks/featured-categories.thunk";
import { setCurrentPage } from "@app/redux/reducers/featured-categories";
import { platformTranslationKeys } from "@app/constants/platform";
import { CategoryFilterBar } from "@app/modules/featured-category-filter-bar/featured-category-filter-bar.component";
import { ROUTES } from "@app/constants/routes";
import { Overview } from "@app/components/overview/overview";
import { TableActions } from "@app/core/table-actions";
import { useOnButtonLinkClick } from "@app/util/use-on-button-link-click";
import { useFilterState } from "@app/util/use-filter-state";

import tableStyles from "./table-styling-component.module.scss";

const TAKE = 30;

const FeaturedCategoryOverview = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const {
    featuredCategories,
    isLoadingFeaturedCategories,
    totalResults,
    currentMerchant,
    currentPlatform,
    currentSearch,
    currentPage
  } = useAppSelector((state) => state.featuredCategories, shallowEqual);

  const { onButtonLinkClick } = useOnButtonLinkClick();
  const [totalPages, setTotalPages] = useState<number>(0);
  const [isDeleteFeaturedCategoryOpen, setIsDeleteFeaturedCategoryOpen] = useState<boolean>(false);
  const [featuredCategoryToDelete, setFeaturedCategoryToDelete] = useState<OutFeaturedCategoriesDTO | undefined>(
    undefined
  );

  useFilterState({
    deps: { currentPlatform, currentSearch, currentMerchant },
    onFirstChange: () => getFeaturedCategories(currentPage),
    onChange: () => getFeaturedCategories(1)
  });

  useEffect(() => {
    setTotalPages(totalResults / TAKE);
  }, [totalResults]);

  const onDeleteFeaturedCategory = (featuredCategory?: OutFeaturedCategoriesDTO) => {
    if (featuredCategory) {
      setFeaturedCategoryToDelete(featuredCategory);
      setIsDeleteFeaturedCategoryOpen(true);
    }
  };

  const onDeleteConfirmed = async () => {
    if (featuredCategoryToDelete) {
      await dispatch(featuredCategoriesThunk.deleteFeaturedCategory(featuredCategoryToDelete.id));

      setIsDeleteFeaturedCategoryOpen(false);
    }
  };

  const getFeaturedCategories = (newPage?: number) => {
    const page = newPage || currentPage;
    const skip = (page - 1) * TAKE;
    dispatch(
      featuredCategoriesThunk.getFeaturedCategories(skip, TAKE, currentMerchant, currentSearch, currentPlatform)
    );
    dispatch(setCurrentPage(page));

    window.scrollTo(0, 0);
  };

  const onPageChange = (page: number) => {
    getFeaturedCategories(page);
  };

  const onTableRowAuxClick = (row: OutFeaturedCategoriesDTO, e: React.MouseEvent<HTMLTableCellElement, MouseEvent>) =>
    onTableRowClick(row, e, true);

  const onTableRowClick = (
    row: OutFeaturedCategoriesDTO,
    e: React.MouseEvent<HTMLTableCellElement, MouseEvent>,
    forceTargetBlank?: boolean
  ) => {
    const route = `${ROUTES.FEATURED_CATEGORY_OVERVIEW}/${row.id}`;

    onButtonLinkClick(route, e, forceTargetBlank);
  };

  return (
    <Overview
      title={intl.formatMessage(
        { id: "categoriesFilterBarComponent.title" },
        { total: intl.formatNumber(totalResults) }
      )}
      actionBar={<CategoryFilterBar />}
      isLoading={isLoadingFeaturedCategories}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      overviewItems={featuredCategories}
    >
      {featuredCategories && featuredCategories.length > 0 && (
        <Table dataSource={featuredCategories || []} isLoading={isLoadingFeaturedCategories}>
          <Column
            title={getColumns(intl).id.title}
            field={getColumns(intl).id.field}
            id={getColumns(intl).id.id}
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(name): JSX.Element => {
              return (
                <span className={clsx(tableStyles.id)} title={name}>
                  {name || "-"}
                </span>
              );
            }}
          />

          <Column
            title={getColumns(intl).slug.title}
            field={getColumns(intl).slug.field}
            id={getColumns(intl).slug.id}
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(name): JSX.Element => {
              return (
                <span className={clsx("truncate-table", tableStyles.slug)} title={name}>
                  {name || "-"}
                </span>
              );
            }}
          />

          <Column
            title={getColumns(intl).title.title}
            field={getColumns(intl).title.field}
            id={getColumns(intl).title.id}
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(_, __, featuredCategory: OutFeaturedCategoriesDTO) => {
              return (
                <span className={clsx(tableStyles.title)} title={featuredCategory.categoryTitle?.toString()}>
                  {featuredCategory.categoryTitle || "-"}
                </span>
              );
            }}
          />

          <Column
            title={getColumns(intl).store.title}
            field={getColumns(intl).store.field}
            id={getColumns(intl).store.id}
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(_, __, featuredCategory: OutFeaturedCategoriesDTO) => {
              return (
                <span
                  className={clsx("truncate-table", tableStyles.store)}
                  title={featuredCategory.merchant.name.toString()}
                >
                  {featuredCategory.merchant.name}
                </span>
              );
            }}
          />
          <Column
            title={getColumns(intl).trackingUrl.title}
            field={getColumns(intl).trackingUrl.field}
            id={getColumns(intl).trackingUrl.id}
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(_, __, featuredCategory: OutFeaturedCategoriesDTO) => {
              return (
                <span
                  className={clsx("truncate-table", tableStyles.trackingUrl)}
                  title={featuredCategory.trackingUrl?.toString()}
                >
                  {featuredCategory.trackingUrl}
                </span>
              );
            }}
          />

          <Column
            title={getColumns(intl).platform.title}
            field={getColumns(intl).platform.field}
            id={getColumns(intl).platform.id}
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(_, __, featuredCategory: OutFeaturedCategoriesDTO) => {
              const platformString =
                featuredCategory &&
                featuredCategory.platform &&
                platformTranslationKeys.find((platformItem) => platformItem.id === featuredCategory.platform.id);

              return (
                <span className={clsx("truncate-table", tableStyles.platform)}>
                  {platformString ? intl.formatMessage({ id: platformString.name }) : "-"}
                </span>
              );
            }}
          />

          <Column
            title=""
            field="id"
            cell={(_, __, featuredCategory: OutFeaturedCategoriesDTO) => {
              return (
                <TableActions
                  editLink={`${ROUTES.FEATURED_CATEGORY_OVERVIEW}/${featuredCategory.id}`}
                  onDelete={() => onDeleteFeaturedCategory(featuredCategory)}
                />
              );
            }}
          />
        </Table>
      )}

      <ModalComponent
        title={intl.formatMessage({ id: "categoriesOverviewComponent.leavePageConfirmation" })}
        isModalOpen={isDeleteFeaturedCategoryOpen}
        onCloseModal={() => {
          setIsDeleteFeaturedCategoryOpen(false);
        }}
        variant="big"
      >
        <DeleteModal
          onCancel={() => {
            setIsDeleteFeaturedCategoryOpen(false);
          }}
          onDelete={onDeleteConfirmed}
        />
      </ModalComponent>
    </Overview>
  );
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getColumns = (intl: IntlShape) => ({
  id: {
    id: "id",
    title: intl.formatMessage({ id: "categoriesOverviewComponent.tableHeaderId" }),
    field: "id"
  },
  slug: {
    id: "slug",
    title: intl.formatMessage({ id: "categoriesOverviewComponent.tableHeaderSlug" }),
    field: "slug"
  },
  title: {
    id: "title",
    title: intl.formatMessage({ id: "categoriesOverviewComponent.tableHeaderTitle" }),
    field: "title"
  },
  store: {
    id: "store",
    title: intl.formatMessage({ id: "categoriesOverviewComponent.tableHeaderStore" }),
    field: "store"
  },
  trackingUrl: {
    id: "trackingUrl",
    title: intl.formatMessage({ id: "categoriesOverviewComponent.tableHeaderTrackingUrl" }),
    field: "trackingUrl"
  },
  platform: {
    id: "platform",
    title: intl.formatMessage({ id: "categoriesOverviewComponent.tableHeaderPlatform" }),
    field: "platform"
  }
});

export { FeaturedCategoryOverview };
